import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";
import {
  ArrowLeft,
  BarChart3,
  CheckCircle2,
  ClipboardCheck,
  Clock3,
  Target,
  Users,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import {
  ANALYTICS_COLORS,
  AnalyticsDrawer,
  AnalyticsTabs,
  ChartCard,
  EmptyAnalytics,
  MetricCard,
  formatPercent,
} from "@/components/analytics/AnalyticsPrimitives";

const TABS = [
  { value: "overview", label: "Visão geral" },
  { value: "questions", label: "Questões" },
  { value: "students", label: "Alunos" },
];

const STATE_LABEL = {
  NOT_STARTED: "Não iniciou",
  IN_PROGRESS: "Em andamento",
  NEEDS_GRADING: "Aguardando correção",
  GRADED: "Corrigida",
  SUBMITTED: "Enviada",
  AUTO_SUBMITTED: "Enviada (tempo)",
  EXPIRED: "Expirada",
};

function TooltipBox({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-[#E0D9CD] rounded-xl shadow-lg px-3 py-2 text-xs max-w-[240px]">
      {label != null && <p className="font-semibold mb-1 break-words">{label}</p>}
      {payload.map((item) => (
        <p key={`${item.dataKey}-${item.name}`} className="text-[#6B655E]">
          <strong className="text-[#1F1F1F]">{item.name}:</strong> {item.value}
        </p>
      ))}
    </div>
  );
}

function ScatterTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div className="bg-white border border-[#E0D9CD] rounded-xl shadow-lg px-3 py-2 text-xs max-w-[230px]">
      <p className="font-semibold break-words">{row.full_name}</p>
      <p className="text-[#6B655E] mt-1">Frequência: <strong className="text-[#1F1F1F]">{row.attendance_percent}%</strong></p>
      <p className="text-[#6B655E]">Nota: <strong className="text-[#1F1F1F]">{row.score}</strong></p>
    </div>
  );
}

function Loading() {
  return (
    <div className="space-y-4 animate-pulse">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">{[0, 1, 2, 3, 4].map((n) => <div key={n} className="h-28 rounded-2xl bg-[#F0EBE3]" />)}</div>
      <div className="grid xl:grid-cols-2 gap-4"><div className="h-72 rounded-2xl bg-[#F0EBE3]" /><div className="h-72 rounded-2xl bg-[#F0EBE3]" /></div>
    </div>
  );
}

export default function EvaluationAnalytics() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("overview");
  const [question, setQuestion] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    api.get(`/analytics/evaluations/${id}`)
      .then(({ data: response }) => alive && setData(response))
      .catch((err) => alive && setError(formatApiError(err)))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [id]);

  const questionRows = useMemo(() => {
    return [...(data?.questions || [])].sort((a, b) => {
      const ap = a.type === "ESSAY" ? 101 : (a.correct_percent ?? 101);
      const bp = b.type === "ESSAY" ? 101 : (b.correct_percent ?? 101);
      return ap - bp;
    });
  }, [data]);

  if (loading) return <div className="p-4 sm:p-6 md:p-8 max-w-7xl mx-auto"><Loading /></div>;
  if (error || !data) return (
    <div className="p-4 sm:p-6 md:p-8 max-w-7xl mx-auto">
      <Link to="/evaluations" className="inline-flex items-center gap-1 text-sm text-[#6B655E] mb-4 hover:text-[#F75A1B]"><ArrowLeft className="w-4 h-4" /> Voltar para avaliações</Link>
      <EmptyAnalytics title="Não foi possível carregar a análise" description={error || "Tente novamente."} />
    </div>
  );

  const t = data.totals || {};
  const funnel = [
    { label: "Público-alvo", value: t.target || 0 },
    { label: "Iniciaram", value: t.started || 0 },
    { label: "Enviaram", value: t.submitted || 0 },
    { label: "Com nota", value: (t.approved || 0) + (t.reproved || 0) },
  ];
  const statusData = [
    { label: "Não iniciou", value: t.not_done || 0, fill: "#A39A8E" },
    { label: "Em andamento", value: t.in_progress || 0, fill: ANALYTICS_COLORS.blue },
    { label: "P/ corrigir", value: t.needs_grading || 0, fill: ANALYTICS_COLORS.amber },
    { label: "Aprovados", value: t.approved || 0, fill: ANALYTICS_COLORS.green },
    { label: "Reprovados", value: t.reproved || 0, fill: ANALYTICS_COLORS.red },
  ].filter((row) => row.value > 0);

  return (
    <div className="p-4 sm:p-6 md:p-8 max-w-7xl mx-auto" data-testid="evaluation-analytics-page">
      <Link to="/evaluations" className="inline-flex items-center gap-1 text-sm text-[#6B655E] mb-4 hover:text-[#F75A1B]">
        <ArrowLeft className="w-4 h-4" /> Voltar para avaliações
      </Link>

      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-[0.18em] text-[#6B655E] font-medium">Analytics da avaliação</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold inline-flex items-start gap-2 mt-1 break-words">
            <BarChart3 className="w-7 h-7 text-[#F75A1B] shrink-0 mt-1" /> {data.evaluation?.title}
          </h1>
          <p className="text-sm text-[#6B655E] mt-2 max-w-3xl">Participação, desempenho, questões e alunos em uma leitura única, mantendo o detalhe operacional disponível sob demanda.</p>
        </div>
        <span className="text-xs rounded-full bg-[#FFEBE3] text-[#F75A1B] px-3 py-1.5 font-medium">R2.2 · Avaliações</span>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
        <MetricCard label="Público-alvo" value={t.target || 0} icon={Users} tone="orange" />
        <MetricCard label="Participação" value={formatPercent(data.participation_rate)} icon={ClipboardCheck} tone="info" hint={`${t.submitted || 0} envio(s)`} />
        <MetricCard label="Média" value={data.avg_score ?? "Sem nota"} icon={Target} tone="orange" hint="Última tentativa considerada" />
        <MetricCard label="Aprovação" value={formatPercent(data.approval_rate)} icon={CheckCircle2} tone="success" hint={`${t.approved || 0} aprovado(s)`} />
        <MetricCard label="Aguardando correção" value={t.needs_grading || 0} icon={Clock3} tone={(t.needs_grading || 0) > 0 ? "warning" : "neutral"} />
      </div>

      <AnalyticsTabs value={tab} onChange={setTab} items={TABS} />

      {tab === "overview" && (
        <div className="space-y-4 mt-4" role="tabpanel">
          <div className="grid xl:grid-cols-2 gap-4">
            <ChartCard title="Funil de participação" subtitle="Do público-alvo até alunos com resultado calculado.">
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={funnel} layout="vertical" margin={{ top: 8, right: 18, left: 18, bottom: 8 }} accessibilityLayer>
                    <CartesianGrid stroke="#EEE8DE" strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="label" width={95} tick={{ fontSize: 11 }} />
                    <Tooltip content={<TooltipBox />} />
                    <Bar dataKey="value" name="Alunos" fill={ANALYTICS_COLORS.orange} radius={[0, 8, 8, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>

            <ChartCard title="Distribuição das notas" subtitle="Faixas da última tentativa com nota calculada. Não inclui correções pendentes.">
              {(data.score_distribution || []).some((row) => row.count > 0) ? (
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.score_distribution} margin={{ top: 8, right: 8, left: -18, bottom: 8 }} accessibilityLayer>
                      <CartesianGrid stroke="#EEE8DE" strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip content={<TooltipBox />} />
                      <Bar dataKey="count" name="Alunos" fill={ANALYTICS_COLORS.blue} radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : <EmptyAnalytics description="Ainda não há notas calculadas suficientes." />}
            </ChartCard>
          </div>

          <div className="grid xl:grid-cols-2 gap-4">
            <ChartCard title="Situação dos alunos" subtitle="Estados que ajudam a identificar ausência, andamento e pendência de correção.">
              {statusData.length ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={statusData} margin={{ top: 8, right: 8, left: -18, bottom: 8 }} accessibilityLayer>
                      <CartesianGrid stroke="#EEE8DE" strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                      <Tooltip content={<TooltipBox />} />
                      <Bar dataKey="value" name="Alunos" radius={[8, 8, 0, 0]}>
                        {statusData.map((row) => <Cell key={row.label} fill={row.fill} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : <EmptyAnalytics />}
            </ChartCard>

            <ChartCard title="Uso de tentativas" subtitle="Mostra quantos alunos precisaram de 1, 2 ou 3+ tentativas.">
              {(data.attempt_distribution || []).some((row) => row.count > 0) ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.attempt_distribution} margin={{ top: 8, right: 8, left: -18, bottom: 8 }} accessibilityLayer>
                      <CartesianGrid stroke="#EEE8DE" strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="attempts" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                      <Tooltip content={<TooltipBox />} />
                      <Bar dataKey="count" name="Alunos" fill={ANALYTICS_COLORS.amber} radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : <EmptyAnalytics />}
            </ChartCard>
          </div>

          <ChartCard title="Frequência × desempenho" subtitle="Cada ponto é um aluno nesta avaliação. Use como associação visual exploratória, nunca como prova de causalidade.">
            {(data.attendance_performance || []).length >= 3 ? (
              <>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <ScatterChart margin={{ top: 12, right: 18, left: 0, bottom: 12 }} accessibilityLayer>
                      <CartesianGrid stroke="#EEE8DE" strokeDasharray="3 3" />
                      <XAxis type="number" dataKey="attendance_percent" name="Frequência" unit="%" domain={[0, 100]} tick={{ fontSize: 11 }} />
                      <YAxis type="number" dataKey="score" name="Nota" domain={[0, 10]} tick={{ fontSize: 11 }} />
                      <ZAxis range={[60, 60]} />
                      <Tooltip content={<ScatterTooltip />} cursor={{ strokeDasharray: "3 3" }} />
                      <Scatter data={data.attendance_performance} fill={ANALYTICS_COLORS.orange} />
                    </ScatterChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-xs text-[#6B655E] mt-2 rounded-xl bg-[#FAF7F1] border border-[#EBE5DB] p-3">{data.methodology?.attendance_performance_note}</p>
              </>
            ) : <EmptyAnalytics description="São necessários pelo menos 3 alunos com nota e frequência calculadas para esta leitura." />}
          </ChartCard>
        </div>
      )}

      {tab === "questions" && (
        <div className="space-y-3 mt-4" role="tabpanel">
          <div className="doxa-card p-4 text-sm text-[#6B655E]">Questões objetivas são ordenadas pelas que tiveram menor taxa de acerto. Dissertativas mostram somente andamento de correção.</div>
          {questionRows.map((row) => {
            const low = row.type !== "ESSAY" && row.correct_percent != null && row.correct_percent < 60;
            return (
              <button
                key={row.question_id}
                type="button"
                onClick={() => setQuestion(row)}
                className={`doxa-card p-4 sm:p-5 w-full text-left hover:shadow-md transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/40 ${low ? "border-[#E7B7B7]" : ""}`}
                data-testid={`question-analytics-${row.question_id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-xs text-[#6B655E]">Questão {row.order || "—"} · {row.type === "MULTIPLE_CHOICE" ? "Múltipla escolha" : row.type === "TRUE_FALSE" ? "Verdadeiro/Falso" : "Dissertativa"}</p>
                    <p className="font-medium mt-1 break-words">{row.prompt}</p>
                    <p className="text-xs text-[#6B655E] mt-2">{row.answered} resposta(s) · {row.missing} sem resposta</p>
                  </div>
                  {row.type === "ESSAY" ? (
                    <div className="text-right shrink-0"><p className="font-display text-2xl font-bold">{row.pending || 0}</p><p className="text-xs text-[#6B655E]">pendente(s)</p></div>
                  ) : (
                    <div className="text-right shrink-0"><p className={`font-display text-2xl font-bold ${low ? "text-[#C53030]" : "text-[#2F855A]"}`}>{formatPercent(row.correct_percent)}</p><p className="text-xs text-[#6B655E]">acerto</p></div>
                  )}
                </div>
              </button>
            );
          })}
          {!questionRows.length && <EmptyAnalytics description="A avaliação ainda não possui questões analisáveis." />}
        </div>
      )}

      {tab === "students" && (
        <div className="mt-4 doxa-card overflow-hidden" role="tabpanel">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm" data-testid="evaluation-student-results">
              <thead className="bg-[#FAF7F1] text-[#6B655E] text-xs">
                <tr><th className="text-left p-3">Aluno</th><th className="text-left p-3">Situação</th><th className="text-center p-3">Tentativas</th><th className="text-center p-3">Nota</th><th className="text-right p-3">Detalhe</th></tr>
              </thead>
              <tbody>
                {(data.rows || []).map((row) => (
                  <tr key={row.student_id} className="border-t border-[#EBE5DB]">
                    <td className="p-3 font-medium">{row.full_name}</td>
                    <td className="p-3 text-[#6B655E]">{STATE_LABEL[row.status] || row.status}</td>
                    <td className="p-3 text-center">{row.attempts_used || 0}</td>
                    <td className="p-3 text-center font-semibold">{row.final_score ?? "—"}</td>
                    <td className="p-3 text-right"><Link to={`/students/${row.student_id}`} className="text-[#F75A1B] hover:underline">Ver aluno</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-4 text-xs text-[#6B655E] rounded-xl bg-[#FAF7F1] border border-[#EBE5DB] px-4 py-3">
        Base metodológica: <strong>{data.methodology?.attempt_basis_label || "Última tentativa do aluno"}</strong>. Resultados pendentes de correção não entram na média até possuírem nota calculada.
      </div>

      <AnalyticsDrawer
        open={!!question}
        title={question ? `Questão ${question.order || ""}` : "Questão"}
        subtitle={question?.prompt}
        onClose={() => setQuestion(null)}
      >
        {question?.type === "ESSAY" ? (
          <div className="space-y-3">
            <MetricCard label="Respondidas" value={question.answered || 0} tone="info" />
            <MetricCard label="Corrigidas" value={question.graded || 0} tone="success" />
            <MetricCard label="Pendentes" value={question.pending || 0} tone={(question.pending || 0) ? "warning" : "neutral"} />
            <div className="rounded-xl bg-[#FAF7F1] border border-[#EBE5DB] p-3 text-xs text-[#6B655E]">A análise não atribui “acerto” automático a respostas dissertativas. Ela exibe somente estados e pontuação efetivamente corrigida.</div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <MetricCard label="Acertos" value={question?.correct || 0} tone="success" />
              <MetricCard label="Taxa de acerto" value={formatPercent(question?.correct_percent)} tone={question?.correct_percent != null && question.correct_percent < 60 ? "critical" : "success"} />
            </div>
            <ChartCard title="Distribuição das respostas" subtitle="A opção correta aparece em verde; as demais usam tons neutros/atenção.">
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={question?.distribution || []} layout="vertical" margin={{ top: 8, right: 8, left: 30, bottom: 8 }} accessibilityLayer>
                    <CartesianGrid stroke="#EEE8DE" strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} />
                    <YAxis type="category" dataKey="label" width={110} tick={{ fontSize: 10 }} />
                    <Tooltip content={<TooltipBox />} />
                    <Bar dataKey="count" name="Respostas" radius={[0, 8, 8, 0]}>
                      {(question?.distribution || []).map((row) => <Cell key={row.key} fill={row.is_correct ? ANALYTICS_COLORS.green : ANALYTICS_COLORS.amber} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>
          </div>
        )}
      </AnalyticsDrawer>
    </div>
  );
}
